/**
 * ProfilePage
 * Vista para editar el perfil del usuario: nombre, email y contraseña
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import apiClient, { getApiError } from '@/api/client';
import useAuthStore from '@/store/authStore';
import PageHeader from '@/components/common/PageHeader';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshProfile, setUser } = useAuthStore();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setUsername(user.username ?? '');
      setEmail((user as any).email ?? '');
    }
  }, [user]);

  const validate = () => {
    if (!username.trim()) {
      setError('El nombre es obligatorio');
      return false;
    }
    if (!emailRegex.test(email)) {
      setError('Email inválido');
      return false;
    }
    if (newPassword || confirmPassword) {
      if (newPassword.length < 8) {
        setError('La nueva contraseña debe tener al menos 8 caracteres');
        return false;
      }
      if (newPassword !== confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }
      if (!currentPassword) {
        setError('Debe proporcionar la contraseña actual para cambiarla');
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: any = {
        username: username.trim(),
        email: email.trim(),
      };

      if (newPassword) {
        payload.currentPassword = currentPassword;
        payload.newPassword = newPassword;
      }

      // Placeholder endpoint - backend may use /auth/profile or /users/me
      const res = await apiClient.put('/auth/profile', payload);

      // Try to refresh local profile state if backend returns updated user
      try {
        await refreshProfile();
      } catch (_err) {
        // Fallback: if response contains user object set it
        if (res && (res as any).data?.user) {
          setUser((res as any).data.user);
        }
      }

      setSuccess('Perfil actualizado correctamente');
      setNewPassword('');
      setConfirmPassword('');
      setCurrentPassword('');
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} height="100%">
      <PageHeader
        icon={<PersonIcon />}
        title="Mi Perfil"
        subtitle="Actualiza tu nombre, correo o contraseña."
        actions={
          <Button variant="outlined" onClick={() => navigate('/')}>
            Volver
          </Button>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Paper sx={{ p: 2, maxWidth: 700 }}>
        <Stack spacing={2}>
          <TextField
            label="Nombre"
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            size="small"
          />

          <TextField
            label="Email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            type="email"
          />

          <Typography variant="subtitle2">Cambiar contraseña</Typography>

          <TextField
            label="Contraseña actual"
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            size="small"
            type="password"
          />

          <TextField
            label="Nueva contraseña"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            size="small"
            type="password"
            helperText="Dejar vacío para mantener la contraseña actual"
          />

          <TextField
            label="Confirmar nueva contraseña"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
            type="password"
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate('/')} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Guardar'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
